<template lang='pug'>
  .products-grid
    template(v-if='productsLoading')
      .product-index-loader
        .loader-spinner

    template(v-else)
      ProductGridEmpty(v-if='productsCount === 0')

      template(v-else)
        .row(v-if='selectedVariants.length == 0')
          .col-6.col-lg-4.product-item(v-for='(product, index) in products' :key='product.id')
            product-card(
              :product='product'
              :variants='variantsForProduct(product)'
              size='lg'
              :early-access-badge='earlyAccessBadge(product)'
              :show-atb='!showRemindMe(product)'
              :show-remind-me='showRemindMe(product)'
              :is-app-exclusive='isAppExclusive(product)'
              :early-access-notification-event-details='earlyAccessNotificationEventDetails(product)'
              :lazy='lazyImages'
              @click.native='emitPosition(index)'
            )
        .row(v-else)
          .col-6.col-lg-4.product-item(v-for='(variant, index) in selectedVariants' :key='variant.id')
            product-card(
              :product='productForVariant(variant)'
              :variants='variantsForProduct(productForVariant(variant))'
              size='lg'
              :early-access-badge='earlyAccessBadge(variant)'
              :show-atb='!showRemindMe(variant)'
              :show-remind-me='showRemindMe(variant)'
              :is-app-exclusive='isAppExclusive(variant)'
              :early-access-notification-event-details='earlyAccessNotificationEventDetails(variant)'
              :selected-variant='variant'
              :lazy='lazyImages'
              @click.native='emitPosition(index)'
            )
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import ProductCard from '../product-card.vue'
import ProductGridEmpty from '../product-grid-empty.vue'
import {
  LaunchStatus,
  LAUNCH_CONFIG_PLATFORM
} from '@/constants/productLaunchConstants'

export default {
  components: {
    ProductCard,
    ProductGridEmpty
  },

  computed: {
    ...mapState({
      currentUser: state => state.user.currentUser,
      products: state => state.product.products,
      productsCount: state => state.product.productsCount,
      productsLoading: state => state.product.productsLoading,
      selectedVariants: state => state.product.selectedVariants
    }),
    ...mapGetters([
      'variantsForProduct',
      'productForVariant'
    ]),
    isMobile: () => vueSupport.isMobile,
    lazyImages: () => !vueSupport.isPrerender
  },

  methods: {
    emitPosition (index) {
      // Using $listeners instead of $emit as $emit has issue for the case when we click a product and
      // redirect back to listing page and click another product here event is not emitted
      // as both $listeners and $emit are almost similar so using $listeners
      this.$listeners.setPosition(index)
    },

    earlyAccessBadge (itemObject) {
      const launchStatus = itemObject?.attributes?.['launch-status']

      if (launchStatus === LaunchStatus.Preview) {
        return this.$t('products.earlyAccess.comingSoon')
      }

      if (launchStatus === LaunchStatus.Prelaunch) {
        const launchConfig = itemObject?.attributes?.['launch-config']

        return this.isQualifiedUser(launchConfig)
          ? this.$t('products.earlyAccess.priorityAccess')
          : this.$t('products.earlyAccess.comingSoon')
      }

      return ''
    },

    showRemindMe (itemObject) {
      const launchStatus = itemObject?.attributes?.['launch-status']

      if (launchStatus === LaunchStatus.Preview) return true

      if (launchStatus === LaunchStatus.Prelaunch) {
        const launchConfig = itemObject?.attributes?.['launch-config']

        return !(this.isQualifiedUser(launchConfig) && this.isValidPlatform(launchConfig))
      }

      return false
    },

    isValidPlatform (launchConfig) {
      return launchConfig?.prelaunch?.platforms?.some(
        (platform) => LAUNCH_CONFIG_PLATFORM.includes(platform)
      )
    },

    isAppExclusive (itemObject) {
      const launchStatus = itemObject?.attributes?.['launch-status']
      const launchConfig = itemObject?.attributes?.['launch-config']

      if (launchStatus === LaunchStatus.Prelaunch) {
        return this.isQualifiedUser(launchConfig) && !this.isValidPlatform(launchConfig)
      }

      return false
    },

    earlyAccessNotificationEventDetails (itemObject) {
      const launchConfig = itemObject?.attributes?.['launch-config']

      const type = this.isQualifiedUser(launchConfig) ? 'prelaunch' : 'launch'

      return {
        eventType: `products_${type}`,
        eventName: launchConfig?.[type]?.name || ''
      }
    },

    isQualifiedUser (launchConfig) {
      const userLoyaltyTier = this.currentUser?.attributes?.['loyalty-tier']
      return !!(
        this.currentUser &&
        userLoyaltyTier &&
        launchConfig?.prelaunch?.tiers?.includes(userLoyaltyTier)
      )
    }
  }
}
</script>

<style lang='scss'>
  .products-grid {
    .product-card-image-container {
      min-height: 140px;

      @include media-breakpoint-up(sm) {
        min-height: 160px;
      }

      @include media-breakpoint-up(md) {
        min-height: 200px;
      }

      @include media-breakpoint-up(xl) {
        min-height: 245px;
      }
    }

    .product-promo {
      min-height: 31px;
    }
  }
</style>

<style scoped lang='scss'>
  .product-index-loader {
    display: flex;
    justify-content: center;
    min-height: 400px;
    padding-top: 40px;
  }

  .product-item {
    margin-bottom: 25px;

    @include media-breakpoint-up(md) {
      margin-bottom: 50px;
      padding-left: 20px;
      padding-right: 20px;
    }

    @include media-breakpoint-up(lg) {
      margin-bottom: 60px;
    }

    @include media-breakpoint-up(xl) {
      margin-bottom: 70px;
    }
  }
</style>
